/**
 * Profile data.
 *
 * Everything the profile page needs, assembled into one shape, so the page
 * itself never has to know which parts are real yet and which are standing in.
 *
 * Real today:
 *		Full name, Email, Firm name		GET /Users/GetUser/v2
 *		Change password					POST /User/ChangePassword/v1
 *
 * Waiting on an API (marked MOCK below):
 *		Job title, team/department, firm context (roles, approvals, billing
 *		increment, visit capture), notification states, last sign-in.
 *
 * When those endpoints land, the mock blocks are the only thing that changes -
 * the page, the cards and the display maps all stay as they are.
 */

import api2 from "/api2.js";
import globalStore from "/globalStore.js";
import { PROFILE_CAPABILITY } from "./profile-config.js";


/**
 * Notification toggles are wired to a mock while the endpoint is built. Flip
 * this to false and the real POST goes out - the request shape is already what
 * the endpoint will take.
 */
const USE_MOCK_NOTIFICATION_API = true;


// One load per page visit. The sidebar user menu and the profile page both want
// the same record, and neither should cost a second round trip.
let cachedProfile = null;




//-- Methods

/**
 * The signed-in person's profile.
 *
 * @param {boolean} options.force Re-fetch even when it's already been loaded.
 * @returns {object|null} The profile, or null when the call failed (already
 * toasted and logged inside api2.js).
 */
export async function getProfile(options = {})
{
	if (cachedProfile != null && options.force !== true) return cachedProfile;

	const request = {
		userId: globalStore.state.currentUser.userId,
	};

	const responseData = await api2.callApi1("GET", "/Users/GetUser/v2", request);
	if (responseData == null) return null;

	cachedProfile = buildProfile(responseData.User);

	return cachedProfile;
}



/**
 * The profile if it has already been loaded, without triggering a call. For
 * callers that can happily render nothing (the sidebar's firm line).
 */
export function getCachedProfile()
{
	return cachedProfile;
}



/**
 * Drop the cache - after a sign-out, or when a save has changed the record.
 */
export function clearProfileCache()
{
	cachedProfile = null;
}



/**
 * Assemble the page's shape from the user record, filling in the parts that
 * have no API yet.
 */
function buildProfile(user)
{
	console.log("buildProfile(): user=", user);
	const record = user ? user : {};

	return {
		Identity: {
			FullName: record.DisplayName ? record.DisplayName : "",
			Email: record.Email ? record.Email : "",

			// MOCK — no API for a person's job title or team yet.
			// JobTitle: "Partner",
			// Team: "Corporate Advisory",
			JobTitle: user.OrganisationRole.Title,
			Team: "",
		},

		Firm: {
			// Real — the account the person belongs to.
			Name: record.AccountName ? record.AccountName : "",

			// MOCK — firm context, all of it, until the API exposes it.
			RoleCodes: ["Timekeeper", "Approver"],
			TimesheetApprovalsState: "On",
			BillingIncrementMinutes: 6,
			VisitCaptureState: "Enabled",
		},

		// MOCK — which optional rows the person should see. Derived from the firm
		// context above so the two can't drift while both are standing in.
		Capabilities: buildCapabilities(["Timekeeper", "Approver"], "Enabled"),

		// MOCK — the person's saved notification preferences, keyed by the code
		// the toggle endpoint takes.
		Notifications: {
			TimesheetSubmissionReminders: "On",
			ApprovalOutcomes: "On",
			SubmissionsAwaitingReview: "On",
			ArrivalAndDeparture: "Off",
			StaleVisitNudges: "On",
		},

		Security: {
			// MOCK — no sign-in history endpoint yet.
			LastSignIn: {
				AtUtc: new Date(Date.now() - (1000 * 60 * 60 * 30)).toISOString(),
				Device: "MacBook Pro",
				OperatingSystem: "macOS",
				Location: "London",
			},
		},
	};
}



/**
 * Turn what the firm says about a person into the capability codes that decide
 * which notification rows they see.
 */
function buildCapabilities(roleCodes, visitCaptureState)
{
	const capabilities = [];

	if (roleCodes && roleCodes.includes("Approver")) capabilities.push(PROFILE_CAPABILITY.Approver);
	if (visitCaptureState === "Enabled") capabilities.push(PROFILE_CAPABILITY.SiteVisitCapture);

	return capabilities;
}



/**
 * Turn a notification on or off.
 *
 * The endpoint takes the code and the state it should end up in - not a
 * "toggle" - so a retry or a double click can't flip it back.
 *
 * @param {string} notificationCode A `Code` from the notification catalogue.
 * @param {string} state "On" or "Off" (see profile-display-values.js).
 * @returns {boolean} True when the change was saved.
 */
export async function saveNotificationState(notificationCode, state)
{
	const request = {
		NotificationCode: notificationCode,
		State: state,
	};

	if (USE_MOCK_NOTIFICATION_API)
	{
		console.log("saveNotificationState() [MOCK]: request=", request);

		// Stand in for the round trip so the UI behaves the way it will in the end.
		await new Promise((resolve) => setTimeout(resolve, 250));

		if (cachedProfile != null) cachedProfile.Notifications[notificationCode] = state;

		return true;
	}

	const responseData = await api2.callApi1("POST", "/Users/Notifications/Toggle", request);
	if (responseData == null) return false;

	if (cachedProfile != null) cachedProfile.Notifications[notificationCode] = state;

	return true;
}



/**
 * Change the signed-in person's password.
 *
 * @returns {object} `{ Success, Reason }` — Reason is the API's own wording for
 * why it was refused, ready to show.
 */
export async function changePassword(currentPassword, newPassword, reEnterPassword)
{
	const request = {
		CurrentPassword: currentPassword,
		NewPassword: newPassword,
		ReEnterPassword: reEnterPassword,
	};

	const responseData = await api2.callApi2WithOverlay("POST", "/User/ChangePassword/v1", request);
	if (responseData == null) return { Success: false, Reason: "" };

	return {
		Success: responseData.Success === true,
		Reason: responseData.UserDisplayDescription ? responseData.UserDisplayDescription : "",
	};
}
